<template>
  <div class="reporte-general">
    <div class="header">
      <h1>📊 Reporte General</h1>
      <div class="fecha-generacion">
        <span>Generado el: {{ formatearFecha(reporte.fechaGeneracion) }}</span>
        <button @click="cargarReporte" class="btn-refresh">🔄 Actualizar</button>
      </div>
    </div>

    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Cargando reporte...</p>
    </div>

    <div v-else-if="reporte.fechaGeneracion" class="reporte-content">
      <!-- Resumen General -->
      <div class="resumen-cards">
        <div class="card resumen-alumnos">
          <div class="card-header">
            <h3>👨‍🎓 Alumnos</h3>
          </div>
          <div class="card-body">
            <div class="stat-item total">
              <span class="value">{{ (reporte.resumenAlumnos && reporte.resumenAlumnos.totalAlumnos) || 0 }}</span>
              <span class="label">Total</span>
            </div>
            <div class="stats-grid">
              <div class="stat-item pagados">
                <span class="value">{{ (reporte.resumenAlumnos && reporte.resumenAlumnos.totalAlumnosPagados) || 0 }}</span>
                <span class="label">Pagados</span>
              </div>
              <div class="stat-item proximos-vencer">
                <span class="value">{{ (reporte.resumenAlumnos && reporte.resumenAlumnos.totalAlumnosProximosVencer) || 0 }}</span>
                <span class="label">Próximos a Vencer</span>
              </div>
              <div class="stat-item vencidos">
                <span class="value">{{ (reporte.resumenAlumnos && reporte.resumenAlumnos.totalAlumnosVencidos) || 0 }}</span>
                <span class="label">Vencidos</span>
              </div>
            </div>
          </div>
        </div>

        <div class="card resumen-maestros">
          <div class="card-header">
            <h3>👨‍🏫 Maestros</h3>
          </div>
          <div class="card-body">
            <div class="stat-item total">
              <span class="value">{{ (reporte.resumenMaestros && reporte.resumenMaestros.totalMaestros) || 0 }}</span>
              <span class="label">Total Activos</span>
            </div>
          </div>
        </div>

        <div class="card distribucion-talleres">
          <div class="card-header">
            <h3>📈 Distribución por Talleres</h3>
          </div>
          <div class="card-body">
            <div class="distribucion-grid">
              <div class="stat-item">
                <span class="value">{{ (reporte.distribucionporTaller && reporte.distribucionporTaller.sintallerasignado) || 0 }}</span>
                <span class="label">S/TALLER</span>
              </div>
              <div class="stat-item">
                <span class="value">{{ (reporte.distribucionporTaller && reporte.distribucionporTaller['1taller']) || 0 }}</span>
                <span class="label">1 Taller</span>
              </div>
              <div class="stat-item">
                <span class="value">{{ (reporte.distribucionporTaller && reporte.distribucionporTaller['2talleres']) || 0 }}</span>
                <span class="label">2 Talleres</span>
              </div>
              <div class="stat-item">
                <span class="value">{{ (reporte.distribucionporTaller && reporte.distribucionporTaller['+3talleres']) || 0 }}</span>
                <span class="label">3+ Talleres</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reporte por Taller -->
      <div class="section">
        <h2>🎯 Reporte por Taller</h2>
        <div class="talleres-grid">
          <div 
            v-for="taller in (reporte.reportePorTaller || [])" 
            :key="taller.idTaller" 
            class="taller-card clickable"
            @click="verDetallesPagosTaller(taller)"
          >
            <div class="taller-header">
              <h4>{{ taller.nombreTaller }}</h4>
              <span class="maestro">{{ taller.maestro }}</span>
            </div>
            <div class="taller-stats">
              <div class="stat">
                <span class="numero">{{ taller.totalAlumnos }}</span>
                <span class="etiqueta">Total</span>
              </div>
              <div class="stat pagados">
                <span class="numero">{{ taller.alumnosPagados }}</span>
                <span class="etiqueta">Pagados</span>
              </div>
              <div class="stat vencidos">
                <span class="numero">{{ taller.alumnosVencidos }}</span>
                <span class="etiqueta">Vencidos</span>
              </div>
            </div>
            <div class="progreso">
              <div class="barra-progreso">
                <div 
                  class="barra-fill"
                  :style="{ width: calcularPorcentajePagados(taller) + '%' }"
                ></div>
              </div>
              <span class="porcentaje">{{ calcularPorcentajePagados(taller) }}% pagados</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagos Recientes -->
      <div class="section">
        <h2>💰 Pagos Recientes</h2>
        <div class="pagos-recientes">
          <div class="tabla-responsive">
            <table class="tabla-pagos">
              <thead>
                <tr>
                  <th>Alumno</th>
                  <th>Cantidad</th>
                  <th>Fecha de Pago</th>
                  <th>Próxima Fecha</th>
                  <th>Cajero</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pago in (reporte.pagosRecientes || [])" :key="pago.idPago">
                  <td class="nombre-alumno">{{ pago.nombreAlumno }}</td>
                  <td class="cantidad">
                    <span v-if="pago.cantidadPagada" class="monto">${{ pago.cantidadPagada.toLocaleString() }}</span>
                    <span v-else class="sin-monto">No especificado</span>
                  </td>
                  <td>{{ formatearFecha(pago.fechaPago) }}</td>
                  <td>{{ formatearFecha(pago.proximaFechaPago) }}</td>
                  <td>{{ pago.nombreCajero || 'No especificado' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="error">
      <h3>⚠️ Error al cargar el reporte</h3>
      <p>No se pudo obtener la información del reporte general.</p>
      <button @click="cargarReporte" class="btn-retry">Reintentar</button>
    </div>

    <!-- Modal de Detalles de Pagos por Taller -->
    <div v-if="showModalPagosTaller" class="modal-overlay" @click="cerrarModalPagosTaller">
      <div class="modal-content pagos-taller-modal" @click.stop>
        <div class="modal-header">
          <h3>💰 Pagos - {{ tallerSeleccionado.nombreTaller }}</h3>
          <button @click="cerrarModalPagosTaller" class="btn-close">✕</button>
        </div>
        
        <div class="modal-body">
          <div class="resumen-taller">
            <div class="resumen-item">
              <span class="label">Total Alumnos:</span>
              <span class="valor">{{ tallerSeleccionado.totalAlumnos }}</span>
            </div>
            <div class="resumen-item">
              <span class="label">Alumnos Pagados:</span>
              <span class="valor pagados">{{ tallerSeleccionado.alumnosPagados }}</span>
            </div>
            <div class="resumen-item">
              <span class="label">Próximos a Vencer:</span>
              <span class="valor proximo-vencer">{{ tallerSeleccionado.alumnosProximosVencer || 0 }}</span>
            </div>
            <div class="resumen-item">
              <span class="label">Vencidos/Sin Pago:</span>
              <span class="valor vencidos">{{ tallerSeleccionado.alumnosVencidos }}</span>
            </div>
            <!-- <div class="resumen-item">
              <span class="label">Sin Pagos:</span>
              <span class="valor sin-pago">{{ tallerSeleccionado.alumnosSinPago || 0 }}</span>
            </div> -->
            <div class="resumen-item">
              <span class="label">Maestro:</span>
              <span class="valor">{{ tallerSeleccionado.maestro }}</span>
            </div>
          </div>

          <div v-if="loadingPagosTaller" class="loading-pagos">
            <div class="spinner-small"></div>
            <p>Cargando pagos del taller...</p>
          </div>

          <div v-else class="pagos-taller-content">
            <h4>📋 Alumnos del Taller - {{ pagosTaller.length }} registros</h4>
            <div v-if="pagosTaller.length > 0" class="tabla-responsive">
              <table class="tabla-pagos-taller">
                <thead>
                  <tr>
                    <th>Alumno</th>
                    <th>Nivel/Grado</th>
                    <th>Estado</th>
                    <th>Último Pago</th>
                    <th>Próximo Pago</th>
                    <th>Monto</th>
                    <th>Folio</th>
                    <th>Días Venc.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pago in pagosTaller" :key="pago.idAlumno">
                    <td class="nombre-alumno">{{ pago.nombreAlumno }}</td>
                    <td class="nivel-grado">{{ pago.nivel || 'N/A' }} - {{ pago.grado || 'N/A' }}</td>
                    <td class="estado-pago">
                      <span class="badge" :class="getBadgeClass(pago.estadoPago)">
                        {{ pago.estadoPago || 'SIN ESTADO' }}
                      </span>
                    </td>
                    <td class="fecha-pago">
                      <span class="fecha">{{ formatearFecha(pago.fechaPago) }}</span>
                    </td>
                    <td class="proxima-fecha">
                      <span class="fecha" :class="{ 'vencido': pago.estadoPago === 'VENCIDO' }">
                        {{ formatearFecha(pago.proximaFechaPago) }}
                      </span>
                      <small v-if="pago.diasHastaVencimiento !== undefined" class="dias-venc">
                        ({{ pago.diasHastaVencimiento }} días)
                      </small>
                    </td>
                    <td class="monto">
                      <span v-if="pago.cantidadPagada" class="cantidad">${{ pago.cantidadPagada.toLocaleString() }}</span>
                      <span v-else class="sin-monto">$0</span>
                    </td>
                    <td class="folio">
                      <span v-if="pago.folio" class="numero-folio">#{{ pago.folio }}</span>
                      <span v-else class="sin-folio">-</span>
                    </td>
                    <td class="dias-vencimiento" :class="getDiasVencimientoClass(pago.diasHastaVencimiento)">
                      {{ pago.diasHastaVencimiento !== undefined ? pago.diasHastaVencimiento : '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="sin-pagos">
              <p>😔 No hay registros de alumnos para este taller</p>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="exportarPagosTaller" class="btn-exportar">
            📊 Exportar Excel
          </button>
          <button @click="cerrarModalPagosTaller" class="btn-cerrar">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '../restapi.js';

export default {
  name: 'ReporteGeneral',
  data() {
    return {
      reporte: {
        fechaGeneracion: null,
        resumenAlumnos: {},
        resumenMaestros: {},
        reportePorTaller: [],
        distribucionporTaller: {},
        pagosRecientes: []
      },
      loading: false,
      // Nuevas propiedades para el modal de pagos por taller
      showModalPagosTaller: false,
      tallerSeleccionado: {},
      pagosTaller: [],
      loadingPagosTaller: false
    };
  },
  methods: {
    async cargarReporte() {
      this.loading = true;
      try {
        const response = await apiFetch('/reportegeneral/ObtenerReporteGeneral');
        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status}`);
        }
        const data = await response.json();

        this.reporte = data;
        console.log('Reporte cargado:', this.reporte);
      } catch (error) {
        console.error('Error al cargar reporte general:', error);
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Error al cargar el reporte general');
        }
      } finally {
        this.loading = false;
      }
    },

    formatearFecha(fecha) {
      if (!fecha) return 'No especificado';
      // Crear la fecha tomando solo la parte de la fecha sin considerar la hora/zona horaria
      const fechaStr = fecha.split('T')[0]; // Obtiene solo "2025-09-01"
      const [year, month, day] = fechaStr.split('-');
      // Crear fecha local directamente sin conversión de zona horaria
      const fechaLocal = new Date(year, month - 1, day);
      return fechaLocal.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    calcularPorcentajePagados(taller) {
      if (taller.totalAlumnos === 0) return 0;
      return Math.round((taller.alumnosPagados / taller.totalAlumnos) * 100);
    },

    async verDetallesPagosTaller(taller) {
      this.tallerSeleccionado = { ...taller };
      this.showModalPagosTaller = true;
      await this.cargarPagosTaller(taller.idTaller);
    },

    async cargarPagosTaller(idTaller) {
      this.loadingPagosTaller = true;
      try {
        const response = await apiFetch(`/pagos/ObtenerPagosAlumnosPorTaller/${idTaller}?page=1&pageSize=100`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Adaptar la estructura de datos de la nueva API
        if (data && data.pagosAlumnos) {
          this.pagosTaller = data.pagosAlumnos.map(alumno => {
            // Determinar el estado real del alumno
            let estadoCalculado = alumno.estadoPago;
            
            // Si no tiene pagos o el monto es 0/null, es SIN_PAGO
            if (!alumno.cantidadPagadaUltimoPago || alumno.cantidadPagadaUltimoPago === 0) {
              estadoCalculado = 'SIN_PAGO';
            }
            // Si tiene días hasta vencimiento entre 1 y 7, es PROXIMO_VENCER
            else if (alumno.diasHastaVencimiento > 0 && alumno.diasHastaVencimiento <= 7) {
              estadoCalculado = 'PROXIMO_VENCER';
            }
            
            return {
              idAlumno: alumno.IdAlumno,
              nombreAlumno: alumno.nombreAlumno,
              nivel: alumno.nivelAcademico,
              grado: alumno.grado,
              fechaPago: alumno.ultimaFechaPago,
              proximaFechaPago: alumno.proximafechadepago,
              cantidadPagada: alumno.cantidadPagadaUltimoPago,
              estadoPago: estadoCalculado,
              estadoOriginal: alumno.estadoPago, // Mantener el estado original
              diasHastaVencimiento: alumno.diasHastaVencimiento,
              folio: alumno.folio,
              fechaCreacion: alumno.ultimaFechaCreacion,
              tipoDepago: alumno.tipoDepago,
              formaDePago: alumno.formaDePago,
              historialPagos: alumno.historialPagos,
              // Campos para mantener compatibilidad con la tabla actual
              nombreCajero: 'No especificado' // Este campo no viene en la nueva API
            };
          });
          
          // Actualizar información del taller seleccionado con los datos de la API
          if (data.taller) {
            // Calcular estadísticas mejoradas
            const alumnosPagados = this.pagosTaller.filter(a => 
              a.estadoPago === 'PAGADO' || (a.cantidadPagada > 0 && a.diasHastaVencimiento > 7)
            ).length;
            
            const alumnosProximosVencer = this.pagosTaller.filter(a => 
              a.estadoPago === 'PROXIMO_VENCER'
            ).length;
            
            const alumnosVencidos = this.pagosTaller.filter(a => 
              a.estadoPago === 'VENCIDO' || a.estadoPago === 'SIN_PAGO'
            ).length;
            
            this.tallerSeleccionado = {
              ...this.tallerSeleccionado,
              idTaller: data.taller.id,
              nombreTaller: data.taller.descripcion,
              maestro: `ID: ${data.taller.idMaestro}`, // Solo tenemos el ID del maestro
              totalAlumnos: data.total,
              alumnosPagados: alumnosPagados,
              alumnosProximosVencer: alumnosProximosVencer,
              alumnosVencidos: alumnosVencidos,
              alumnosSinPago: this.pagosTaller.filter(a => a.estadoPago === 'SIN_PAGO').length
            };
          }
          
          console.log('Pagos del taller cargados:', this.pagosTaller);
          console.log('Información del taller:', data.taller);
          console.log('Total de registros:', data.total);
        } else {
          this.pagosTaller = [];
        }
        
      } catch (error) {
        console.error('Error al cargar pagos del taller:', error);
        this.pagosTaller = [];
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Error al cargar los pagos del taller');
        }
      } finally {
        this.loadingPagosTaller = false;
      }
    },

    cerrarModalPagosTaller() {
      this.showModalPagosTaller = false;
      this.tallerSeleccionado = {};
      this.pagosTaller = [];
    },

    estaVencido(fecha) {
      if (!fecha) return false;
      const hoy = new Date();
      const fechaVencimiento = new Date(fecha);
      return fechaVencimiento < hoy;
    },

    getBadgeClass(estadoPago) {
      switch (estadoPago) {
        case 'PAGADO':
          return 'badge-pagado';
        case 'VENCIDO':
          return 'badge-vencido';
        case 'PENDIENTE':
          return 'badge-pendiente';
        case 'PROXIMO_VENCER':
          return 'badge-proximo-vencer';
        case 'SIN_PAGO':
          return 'badge-sin-pago';
        default:
          return 'badge-default';
      }
    },

    getDiasVencimientoClass(dias) {
      if (dias === undefined || dias === null) return '';
      if (dias < 0) return 'vencido';
      if (dias <= 5) return 'proximo-vencer';
      return 'vigente';
    },

    async exportarPagosTaller() {
      try {
        const ExcelJS = await import('exceljs');
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(`Pagos - ${this.tallerSeleccionado.nombreTaller}`);
        
        // Configurar encabezados
        worksheet.columns = [
          { header: 'Alumno', key: 'alumno', width: 25 },
          { header: 'Nivel/Grado', key: 'nivelGrado', width: 15 },
          { header: 'Estado', key: 'estado', width: 12 },
          { header: 'Último Pago', key: 'ultimoPago', width: 15 },
          { header: 'Próximo Pago', key: 'proximoPago', width: 15 },
          { header: 'Monto', key: 'monto', width: 12 },
          { header: 'Folio', key: 'folio', width: 10 },
          { header: 'Días Vencimiento', key: 'diasVenc', width: 15 }
        ];
        
        // Agregar título
        worksheet.insertRow(1, [`Reporte de Pagos - ${this.tallerSeleccionado.nombreTaller}`]);
        worksheet.insertRow(2, [`Maestro: ${this.tallerSeleccionado.maestro}`]);
        worksheet.insertRow(3, [`Total de alumnos: ${this.pagosTaller.length}`]);
        worksheet.insertRow(4, [`Fecha de generación: ${new Date().toLocaleDateString('es-ES')}`]);
        worksheet.insertRow(5, []);
        
        // Estilo para el título
        worksheet.getRow(1).font = { bold: true, size: 14 };
        worksheet.getRow(2).font = { bold: true };
        worksheet.getRow(3).font = { bold: true };
        worksheet.getRow(4).font = { italic: true };
        
        // Agregar datos
        this.pagosTaller.forEach(pago => {
          worksheet.addRow({
            alumno: pago.nombreAlumno,
            nivelGrado: `${pago.nivel || 'N/A'} - ${pago.grado || 'N/A'}`,
            estado: pago.estadoPago || 'SIN ESTADO',
            ultimoPago: this.formatearFecha(pago.fechaPago),
            proximoPago: this.formatearFecha(pago.proximaFechaPago),
            monto: pago.cantidadPagada ? `$${pago.cantidadPagada.toLocaleString()}` : '$0',
            folio: pago.folio ? `#${pago.folio}` : '-',
            diasVenc: pago.diasHastaVencimiento !== undefined ? pago.diasHastaVencimiento : '-'
          });
        });
        
        // Estilo para encabezados
        const headerRow = worksheet.getRow(6);
        headerRow.font = { bold: true };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4472C4' }
        };
        
        // Generar y descargar archivo
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Pagos_${this.tallerSeleccionado.nombreTaller.replace(/\s+/g, '_')}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        if (this.$toast && this.$toast.success) {
          this.$toast.success('Archivo Excel generado correctamente');
        }
        
      } catch (error) {
        console.error('Error al exportar Excel:', error);
        if (this.$toast && this.$toast.error) {
          this.$toast.error('Error al generar el archivo Excel');
        }
      }
    }
  },

  mounted() {
    this.cargarReporte();
  }
};
</script>

<style scoped>
.reporte-general {
  padding: 2rem;
  background: #f8f9fa;
  min-height: 100vh;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.header h1 {
  margin: 0;
  color: #2c3e50;
  font-size: 2rem;
}

.fecha-generacion {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #6c757d;
}

.btn-refresh {
  background: #28a745;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.3s;
}

.btn-refresh:hover {
  background: #218838;
}

.loading {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.resumen-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: transform 0.3s;
}

.card:hover {
  transform: translateY(-2px);
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1rem;
}

.card-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.card-body {
  padding: 1.5rem;
}

.stat-item {
  text-align: center;
  margin-bottom: 1rem;
}

.stat-item.total .value {
  font-size: 3rem;
  font-weight: bold;
  color: #2c3e50;
  display: block;
}

.stat-item .value {
  font-size: 2rem;
  font-weight: bold;
  display: block;
}

.stat-item .label {
  color: #6c757d;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
}

.stat-item.pagados .value {
  color: #28a745;
}

.stat-item.proximos-vencer .value {
  color: #ffc107;
}

.stat-item.vencidos .value {
  color: #dc3545;
}

.distribucion-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.section {
  margin-bottom: 2rem;
}

.section h2 {
  color: #2c3e50;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
}

.talleres-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

.taller-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: transform 0.3s;
}

.taller-card:hover {
  transform: translateY(-2px);
}

.taller-header h4 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.maestro {
  color: #6c757d;
  font-size: 0.9rem;
  display: block;
  margin-bottom: 1rem;
}

.taller-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.stat {
  text-align: center;
}

.stat .numero {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: #2c3e50;
}

.stat.pagados .numero {
  color: #28a745;
}

.stat.vencidos .numero {
  color: #dc3545;
}

.stat .etiqueta {
  font-size: 0.8rem;
  color: #6c757d;
  text-transform: uppercase;
}

.progreso {
  margin-top: 1rem;
}

.barra-progreso {
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.barra-fill {
  height: 100%;
  background: linear-gradient(90deg, #28a745, #20c997);
  transition: width 0.5s ease;
}

.porcentaje {
  font-size: 0.9rem;
  color: #6c757d;
}

.pagos-recientes {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

.tabla-responsive {
  overflow-x: auto;
}

.tabla-pagos {
  width: 100%;
  border-collapse: collapse;
}

.tabla-pagos th {
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  color: #2c3e50;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
}

.tabla-pagos td {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.nombre-alumno {
  font-weight: 600;
  color: #2c3e50;
}

.monto {
  color: #28a745;
  font-weight: 600;
}

.sin-monto {
  color: #6c757d;
  font-style: italic;
}

.error {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.error h3 {
  color: #dc3545;
  margin-bottom: 1rem;
}

.btn-retry {
  background: #007bff;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 1rem;
}

.btn-retry:hover {
  background: #0056b3;
}

@media (max-width: 768px) {
  .reporte-general {
    padding: 1rem;
  }
  
  .header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .resumen-cards {
    grid-template-columns: 1fr;
  }
  
  .talleres-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .distribucion-grid {
    grid-template-columns: 1fr;
  }
  
  .taller-stats {
    flex-direction: column;
    gap: 1rem;
  }
}

/* Estilos para tarjetas clickables */
.taller-card.clickable {
  cursor: pointer;
  transition: all 0.3s ease;
}

.taller-card.clickable:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

/* Estilos para el modal de pagos por taller */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.pagos-taller-modal {
  background: white;
  border-radius: 16px;
  width: 90vw;
  max-width: 1000px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 2px solid #e9ecef;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
}

.btn-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s;
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.resumen-taller {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.resumen-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.resumen-item .label {
  font-weight: 600;
  color: #495057;
}

.resumen-item .valor {
  font-weight: bold;
  color: #212529;
}

.resumen-item .valor.pagados {
  color: #28a745;
}

.resumen-item .valor.vencidos {
  color: #dc3545;
}

.resumen-item .valor.proximo-vencer {
  color: #f39c12;
}

.resumen-item .valor.sin-pago {
  color: #922b21;
}

.loading-pagos {
  text-align: center;
  padding: 2rem;
}

.spinner-small {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

.pagos-taller-content h4 {
  color: #495057;
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.tabla-pagos-taller {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.tabla-pagos-taller th {
  background: linear-gradient(135deg, #495057 0%, #343a40 100%);
  color: white;
  padding: 1rem 0.8rem;
  font-weight: 600;
  text-align: left;
  font-size: 0.9rem;
}

.tabla-pagos-taller td {
  padding: 0.8rem;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.tabla-pagos-taller tr:hover {
  background: #f8f9fa;
}

.nombre-alumno {
  font-weight: 600;
  color: #495057;
}

.nivel-grado {
  color: #6c757d;
  font-size: 0.9rem;
}

.fecha-pago .fecha {
  color: #495057;
  font-weight: 500;
}

.proxima-fecha .fecha {
  color: #28a745;
  font-weight: 500;
}

.proxima-fecha .fecha.vencido {
  color: #dc3545;
  font-weight: 600;
}

.monto .cantidad {
  color: #007bff;
  font-weight: 600;
}

.monto .sin-monto {
  color: #6c757d;
  font-style: italic;
}

.cajero {
  color: #495057;
}

.sin-pagos {
  text-align: center;
  padding: 3rem 2rem;
  color: #6c757d;
}

.sin-pagos p {
  font-size: 1.1rem;
  margin: 0;
}

/* Nuevos estilos para badges y estados */
.badge {
  padding: 0.3rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-pagado {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.badge-vencido {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.badge-pendiente {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.badge-default {
  background: #e2e3e5;
  color: #383d41;
  border: 1px solid #d6d8db;
}

.badge-proximo-vencer {
  background: #ffeaa7;
  color: #d68910;
  border: 1px solid #f4d03f;
}

.badge-sin-pago {
  background: #fadbd8;
  color: #922b21;
  border: 1px solid #f1948a;
}

.folio .numero-folio {
  color: #007bff;
  font-weight: 600;
  font-family: monospace;
}

.folio .sin-folio {
  color: #6c757d;
}

.dias-vencimiento {
  font-weight: 600;
  text-align: center;
}

.dias-vencimiento.vencido {
  color: #dc3545;
  background: #f8d7da;
  border-radius: 4px;
  padding: 0.2rem 0.4rem;
}

.dias-vencimiento.proximo-vencer {
  color: #856404;
  background: #fff3cd;
  border-radius: 4px;
  padding: 0.2rem 0.4rem;
}

.dias-vencimiento.vigente {
  color: #155724;
}

.dias-venc {
  display: block;
  color: #6c757d;
  font-size: 0.75rem;
  margin-top: 0.2rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 2px solid #e9ecef;
  background: #f8f9fa;
}

.btn-exportar {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-exportar:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.btn-cerrar {
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-cerrar:hover {
  background: #545b62;
  transform: translateY(-2px);
}

/* Responsive para el modal */
@media (max-width: 768px) {
  .pagos-taller-modal {
    width: 95vw;
    max-height: 95vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .resumen-taller {
    grid-template-columns: 1fr;
  }
  
  .tabla-pagos-taller {
    font-size: 0.8rem;
  }
  
  .tabla-pagos-taller th,
  .tabla-pagos-taller td {
    padding: 0.5rem 0.3rem;
  }
  
  .modal-footer {
    padding: 1rem;
    flex-direction: column;
  }
}
</style>